import type { PaymentMethodsResponse, CreatePaymentRequest, CreatePaymentResponse, CustomerData, WalletsResponse, PaymentLinkResponse } from '../types';

const API_PRODUCTION_URL = 'https://api.aloha.co/api/external/v1';
const API_SANDBOX_URL = 'https://api.sandbox.aloha.co/api/external/v1';

function getBaseUrl(useSandbox?: boolean): string {
  return useSandbox ? API_SANDBOX_URL : API_PRODUCTION_URL;
}

export async function getWallets(apiKey: string, useSandbox?: boolean): Promise<WalletsResponse> {
  const response = await fetch(`${getBaseUrl(useSandbox)}/wallets`, {
    method: 'GET',
    headers: {
      'X-API-KEY': apiKey
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch wallets' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function getPaymentMethods(apiKey: string, useSandbox?: boolean): Promise<PaymentMethodsResponse> {
  const response = await fetch(`${getBaseUrl(useSandbox)}/payment-methods`, {
    method: 'GET',
    headers: {
      'X-API-KEY': apiKey
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch payment methods' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function createPaymentRequest(data: CreatePaymentRequest, useSandbox?: boolean): Promise<CreatePaymentResponse> {
  const response = await fetch(`${getBaseUrl(useSandbox)}/payment-links`, {
    method: 'POST',
    headers: {
      'X-API-KEY': data.apiKey,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      amount: data.amount,
      currency: data.clientCurrency,
      description: data.concept || 'Payment',
      source: 'embedded_checkout'
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to create payment' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  const result = await response.json();

  return {
    success: result.success,
    checkoutUrl: result.data.url
  };
}

export async function getPaymentLinkStatus(apiKey: string, paymentLinkId: string, useSandbox?: boolean): Promise<PaymentLinkResponse> {
  const response = await fetch(`${getBaseUrl(useSandbox)}/payment-links/${paymentLinkId}`, {
    method: 'GET',
    headers: {
      'X-API-KEY': apiKey
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch payment status' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return response.json();
}

export function extractPaymentLinkId(checkoutUrl: string): string | null {
  // URL format: https://checkout.aloha.co/s/9d8f7a6b-5c4d-3e2f-1a0b-9c8d7e6f5a4b
  const match = checkoutUrl.match(/\/s\/([a-f0-9-]+)$/i);
  return match ? match[1] : null;
}

export function buildCheckoutUrl(baseUrl: string, customer: CustomerData): string {
  const params = new URLSearchParams();

  if (customer.firstName) params.set('firstname', customer.firstName);
  if (customer.lastName) params.set('lastname', customer.lastName);
  if (customer.email) params.set('email', customer.email);
  if (customer.phone) params.set('phone', customer.phone);

  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}
